import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import type { Portfolio, SimpleFund } from 'venn-api';
import { getFund, getSpecificPortfolioV3 } from 'venn-api';
import { proxySetIdState } from 'venn-state';
import type { BenchmarkConstraint } from 'venn-utils';
import { logExceptionIntoSentry, useApi } from 'venn-utils';
import { isNil } from 'lodash';

interface ConstraintBenchmarkPseudoPortfolioResult {
  portfolio: Portfolio | undefined;
  loading: boolean;
}

const useConstraintBenchmarkPseudoPortfolio = (
  benchmarkConstraint: BenchmarkConstraint | undefined,
  portfolioAllocation: number | undefined,
): ConstraintBenchmarkPseudoPortfolioResult => {
  const [benchmarkPortfolio, setBenchmarkPortfolio] = useState<Portfolio | undefined>();
  const [benchmarkFund, setBenchmarkFund] = useState<SimpleFund | undefined>();
  // The strategyId/fundId that benchmarkPortfolio/benchmarkFund above were actually resolved for.
  // Comparing these against the current constraint (below) gives a `loading` value that's accurate
  // in the very same render the constraint changes, instead of lagging a render behind like a
  // separate `useState` flip inside the effect would - that lag let a stale/mismatched optimization
  // request slip out before the correct one, requiring the first to be aborted.
  const [resolvedStrategyId, setResolvedStrategyId] = useState<number | undefined>();
  const [resolvedFundId, setResolvedFundId] = useState<string | undefined>();

  const proxySetId = useRecoilValue(proxySetIdState);
  const getPortfolioApiRef = useRef(useApi(getSpecificPortfolioV3));
  const getFundApiRef = useRef(useApi(getFund));

  const benchmarkStrategyId = benchmarkConstraint?.benchmarkStrategyId;
  const benchmarkFundId = benchmarkConstraint?.benchmarkFundId;

  useEffect(() => {
    if (isNil(benchmarkStrategyId) && isNil(benchmarkFundId)) {
      setBenchmarkPortfolio(undefined);
      setBenchmarkFund(undefined);
      setResolvedStrategyId(undefined);
      setResolvedFundId(undefined);
      return;
    }

    if (!isNil(benchmarkStrategyId)) {
      setBenchmarkFund(undefined);
      getPortfolioApiRef
        .current(benchmarkStrategyId, undefined, proxySetId)
        .then(({ content }) => {
          setBenchmarkPortfolio(content);
          setResolvedStrategyId(benchmarkStrategyId);
          setResolvedFundId(undefined);
        })
        .catch((e) => {
          if (e?.name === 'AbortError') return;
          logExceptionIntoSentry(e);
          setBenchmarkPortfolio(undefined);
          setResolvedStrategyId(benchmarkStrategyId);
          setResolvedFundId(undefined);
        });
    } else if (!isNil(benchmarkFundId)) {
      setBenchmarkPortfolio(undefined);
      getFundApiRef
        .current(benchmarkFundId, proxySetId)
        .then(({ content }) => {
          setBenchmarkFund(content as SimpleFund);
          setResolvedFundId(benchmarkFundId);
          setResolvedStrategyId(undefined);
        })
        .catch((e) => {
          if (e?.name === 'AbortError') return;
          logExceptionIntoSentry(e);
          setBenchmarkFund(undefined);
          setResolvedFundId(benchmarkFundId);
          setResolvedStrategyId(undefined);
        });
    }
  }, [benchmarkStrategyId, benchmarkFundId, proxySetId]);

  const loading = benchmarkStrategyId !== resolvedStrategyId || benchmarkFundId !== resolvedFundId;

  const portfolio = useMemo<Portfolio | undefined>(() => {
    if (!isNil(benchmarkPortfolio)) return benchmarkPortfolio;
    if (!isNil(benchmarkFund)) {
      const now = Date.now();
      return {
        id: now,
        allocation: portfolioAllocation,
        name: benchmarkFund.name,
        draft: true,
        demo: false,
        master: false,
        closingAllocationsTs: [],
        historical: false,
        absNetAssets: 0,
        designation: 'DEFAULT',
        lots: [],
        taxStatuses: [],
        children: [
          {
            id: now + 1,
            allocation: portfolioAllocation,
            name: benchmarkFund.name,
            fund: benchmarkFund,
            draft: true,
            demo: false,
            master: false,
            children: [],
            closingAllocationsTs: [],
            historical: false,
            absNetAssets: 0,
            designation: 'DEFAULT',
            lots: [],
            taxStatuses: [],
          },
        ],
      };
    }
    return undefined;
  }, [benchmarkPortfolio, benchmarkFund, portfolioAllocation]);

  return { portfolio, loading };
};

export default useConstraintBenchmarkPseudoPortfolio;
